import { useState, useRef, useEffect } from 'react';
import { isFunction, Default, Direction, SyntheticEvent } from '../utils';
function getX(e) {
    return e.targetTouches && e.targetTouches.length >= 1
        ? e.targetTouches[0].clientX
        : e.clientX;
}
function getY(e) {
    return e.targetTouches && e.targetTouches.length >= 1
        ? e.targetTouches[0].clientY
        : e.clientY;
}
export function useToast(props) {
    var _a = useState(false), isRunning = _a[0], setIsRunning = _a[1];
    var _b = useState(false), preventExitTransition = _b[0], setPreventExitTransition = _b[1];
    var toastRef = useRef(null);
    var drag = useRef({
        start: 0,
        x: 0,
        y: 0,
        delta: 0,
        removalDistance: 0,
        canCloseOnClick: true,
        canDrag: false,
        boundingRect: null,
        didMove: false,
    }).current;
    var syncProps = useRef(props);
    var autoClose = props.autoClose, pauseOnHover = props.pauseOnHover, closeToast = props.closeToast, onClick = props.onClick, closeOnClick = props.closeOnClick;
    useEffect(function () {
        syncProps.current = props;
    });
    useEffect(function () {
        if (toastRef.current)
            toastRef.current.addEventListener(SyntheticEvent.ENTRANCE_ANIMATION_END, playToast, { once: true });
        if (isFunction(props.onOpen))
            props.onOpen();
        return function () {
            var props = syncProps.current;
            if (isFunction(props.onClose)) {
                props.onClose();
            }
        };
    }, []);
    useEffect(function () {
        props.pauseOnFocusLoss && bindFocusEvents();
        return function () {
            props.pauseOnFocusLoss && unbindFocusEvents();
        };
    }, [props.pauseOnFocusLoss]);
    function onDragStart(e) {
        if (props.draggable) {
            // required for ios safari to prevent default swipe behavior
            if (e.nativeEvent.type === 'touchstart')
                e.nativeEvent.preventDefault();
            bindDragEvents();
            var toast = toastRef.current;
            drag.canCloseOnClick = true;
            drag.canDrag = true;
            drag.boundingRect = toast.getBoundingClientRect();
            toast.style.transition = '';
            drag.x = getX(e.nativeEvent);
            drag.y = getY(e.nativeEvent);
            if (props.draggableDirection === Direction.X) {
                drag.start = drag.x;
                drag.removalDistance =
                    toast.offsetWidth * (Default.DRAGGABLE_PERCENT / 100);
            }
            else {
                drag.start = drag.y;
                drag.removalDistance =
                    toast.offsetHeight * (Default.DRAGGABLE_PERCENT / 100);
            }
        }
    }
    function onDragTransitionEnd(e) {
        if (drag.boundingRect) {
            var _a = drag.boundingRect, top_1 = _a.top, bottom = _a.bottom, left = _a.left, right = _a.right;
            if (e.nativeEvent.type !== 'touchend' &&
                props.pauseOnHover &&
                drag.x >= left &&
                drag.x <= right &&
                drag.y >= top_1 &&
                drag.y <= bottom) {
                pauseToast();
            }
            else {
                playToast();
            }
        }
    }
    function playToast() {
        setIsRunning(true);
    }
    function pauseToast() {
        setIsRunning(false);
    }
    function bindFocusEvents() {
        if (!document.hasFocus())
            pauseToast();
        window.addEventListener('focus', playToast);
        window.addEventListener('blur', pauseToast);
    }
    function unbindFocusEvents() {
        window.removeEventListener('focus', playToast);
        window.removeEventListener('blur', pauseToast);
    }
    function bindDragEvents() {
        drag.didMove = false;
        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('mouseup', onDragEnd);
        document.addEventListener('touchmove', onDragMove);
        document.addEventListener('touchend', onDragEnd);
    }
    function unbindDragEvents() {
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
        document.removeEventListener('touchmove', onDragMove);
        document.removeEventListener('touchend', onDragEnd);
    }
    function onDragMove(e) {
        var toast = toastRef.current;
        if (drag.canDrag && toast) {
            drag.didMove = true;
            if (isRunning)
                pauseToast();
            drag.x = getX(e);
            drag.y = getY(e);
            if (props.draggableDirection === Direction.X) {
                drag.delta = drag.x - drag.start;
            }
            else {
                drag.delta = drag.y - drag.start;
            }
            // prevent false positif during a toast click
            if (drag.start !== drag.x)
                drag.canCloseOnClick = false;
            toast.style.transform = "translate".concat(props.draggableDirection, "(").concat(drag.delta, "px)");
            toast.style.opacity = "".concat(1 - Math.abs(drag.delta / drag.removalDistance));
        }
    }
    function onDragEnd() {
        unbindDragEvents();
        var toast = toastRef.current;
        if (drag.canDrag && drag.didMove && toast) {
            drag.canDrag = false;
            if (Math.abs(drag.delta) > drag.removalDistance) {
                setPreventExitTransition(true);
                props.closeToast();
                return;
            }
            toast.style.transition = 'transform 0.2s, opacity 0.2s';
            toast.style.transform = "translate".concat(props.draggableDirection, "(0)");
            toast.style.opacity = '1';
        }
    }
    var eventHandlers = {
        onMouseDown: onDragStart,
        onTouchStart: onDragStart,
        onMouseUp: onDragTransitionEnd,
        onTouchEnd: onDragTransitionEnd,
    };
    if (autoClose && pauseOnHover) {
        eventHandlers.onMouseEnter = pauseToast;
        eventHandlers.onMouseLeave = playToast;
    }
    // prevent toast from closing when user drags the toast
    if (closeOnClick) {
        eventHandlers.onClick = function (e) {
            onClick && onClick(e);
            drag.canCloseOnClick && closeToast();
        };
    }
    return {
        playToast: playToast,
        pauseToast: pauseToast,
        isRunning: isRunning,
        preventExitTransition: preventExitTransition,
        toastRef: toastRef,
        eventHandlers: eventHandlers,
    };
}
